import type { CarSpec, Gear, ManualSpec, SimEvent } from './types';
import { isForwardGear } from './types';

/**
 * Tren motriz MANUAL: motor con inercia propia, embrague de fricción con
 * punto de mordida y caja en H. Módulo puro y determinista: mismo estado +
 * misma entrada ⇒ misma salida. Aquí vive la mecánica que enseña el simulador:
 * calarse, patinar el embrague, rascar marchas y el freno motor.
 */

/** Rendimiento mecánico de la transmisión. */
const DRIVELINE_EFF = 0.9;
/** Umbral de bloqueo del embrague (rpm de deslizamiento). */
const LOCK_SLIP_RPM = 45;
/** Velocidad máxima (m/s) hacia delante a la que se permite engranar R. */
const REVERSE_LOCKOUT_MS = 0.9;
/** Duración del movimiento de palanca (s). */
const SHIFT_TIME_S = 0.16;
/** Tiempo de starter necesario para arrancar (s). */
const CRANK_TIME_S = 0.55;
/** Pedal mínimo de embrague para mover la palanca o usar el starter. */
const CLUTCH_FOR_SHIFT = 0.75;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export interface ManualState {
  /** Marcha ENGRANADA ('N' | 'R' | 1..6). */
  gear: Gear;
  /** Palanca en tránsito hacia esta marcha. */
  pendingGear: Gear | null;
  shiftTimerS: number;
  rpm: number;
  engineOn: boolean;
  crankTimerS: number;
  stallCount: number;
  clutchHeat: number;
  /** Acoplamiento real del embrague (sigue al pedal con un pequeño retardo). */
  engagement: number;
  grindCooldownS: number;
  crankBlockCooldownS: number;
  overRevCooldownS: number;
  /** Contador interno para el temblor determinista (pre-calado / ahogado). */
  judderTick: number;
}

export function createManual(spec: CarSpec): ManualState {
  const m = requireManual(spec);
  return {
    gear: 'N',
    pendingGear: null,
    shiftTimerS: 0,
    rpm: m.idleRpm,
    engineOn: true,
    crankTimerS: 0,
    stallCount: 0,
    clutchHeat: 0,
    engagement: 0,
    grindCooldownS: 0,
    crankBlockCooldownS: 0,
    overRevCooldownS: 0,
    judderTick: 0,
  };
}

export function requireManual(spec: CarSpec): ManualSpec {
  if (!spec.manual) throw new Error(`El coche ${spec.id} no tiene spec manual`);
  return spec.manual;
}

export interface ManualInput {
  throttle: number;
  clutch: number; // 0 suelto .. 1 pisado
  ignition: boolean;
  requestedGear: Gear;
  /** Velocidad longitudinal con signo (m/s, + adelante). */
  speedMs: number;
  /** Límite de tracción del eje motriz (N). */
  gripCapN: number;
  dt: number;
}

export interface ManualOutput {
  /** Fuerza en el eje motriz (N, + adelante). Negativa = freno motor. */
  driveForceN: number;
  rpm: number;
  events: SimEvent[];
  engagement: number;
  slipRpm: number;
  atBite: boolean;
  cranking: boolean;
  lugging: boolean;
  overRev: boolean;
  shiftUpHint: boolean;
  shiftDownHint: boolean;
  /** La demanda superó el agarre del eje motriz (patinado de rueda). */
  wheelspin: boolean;
}

/** Interpola la curva de par normalizada (0..1) del motor. */
export function curveTorqueFrac(curve: [number, number][], rpm: number): number {
  const first = curve[0];
  if (!first) return 0;
  if (rpm <= first[0]) return first[1];
  for (let i = 1; i < curve.length; i++) {
    const [r1, f1] = curve[i]!;
    const [r0, f0] = curve[i - 1]!;
    if (rpm <= r1) return f0 + ((f1 - f0) * (rpm - r0)) / Math.max(1, r1 - r0);
  }
  return curve[curve.length - 1]![1];
}

/** Acoplamiento objetivo del embrague según el pedal (0 abierto → 1 cerrado). */
export function clutchTarget(pedal: number, m: ManualSpec): number {
  const hi = m.clutch.bitePoint + m.clutch.biteWidth / 2;
  const lo = m.clutch.bitePoint - m.clutch.biteWidth / 2;
  const t = clamp((hi - pedal) / Math.max(0.01, hi - lo), 0, 1);
  return t * t * (3 - 2 * t); // suavizado tipo smoothstep
}

/** ¿Está el pedal dentro de la zona útil del punto de fricción? */
export function inBiteZone(pedal: number, m: ManualSpec): boolean {
  const hi = m.clutch.bitePoint + m.clutch.biteWidth / 2;
  const lo = m.clutch.bitePoint - m.clutch.biteWidth / 2;
  return pedal <= hi && pedal >= lo;
}

export function gearRatioOf(spec: CarSpec, gear: Gear): number {
  const m = requireManual(spec);
  if (gear === 'R') return m.reverseRatio * spec.finalDrive;
  if (isForwardGear(gear)) {
    const g = spec.gearRatios[gear - 1];
    if (g === undefined) return 0;
    return g * spec.finalDrive;
  }
  return 0;
}

export function manualGearCount(spec: CarSpec): number {
  return spec.gearRatios.length;
}

/** Rpm del lado caja (eje primario) para una marcha y velocidad dadas. */
function shaftRpmFor(spec: CarSpec, gear: Gear, speedMs: number): number {
  const ratio = gearRatioOf(spec, gear);
  if (ratio === 0) return 0;
  const dir = gear === 'R' ? -1 : 1;
  const drivenSpeed = speedMs * dir; // + en el sentido de la marcha engranada
  return (drivenSpeed / (2 * Math.PI * spec.dims.wheelRadiusM)) * 60 * ratio;
}

/** Umbral GSI de subir marcha, derivado del carácter del motor. */
function shiftUpRpm(m: ManualSpec): number {
  return Math.min(3200, m.idleRpm + 0.35 * (m.redlineRpm - m.idleRpm));
}

export function stepManual(st: ManualState, spec: CarSpec, inp: ManualInput): ManualOutput {
  const m = requireManual(spec);
  const { dt } = inp;
  const events: SimEvent[] = [];
  st.judderTick++;
  st.grindCooldownS = Math.max(0, st.grindCooldownS - dt);
  st.crankBlockCooldownS = Math.max(0, st.crankBlockCooldownS - dt);
  st.overRevCooldownS = Math.max(0, st.overRevCooldownS - dt);

  // ------------------------------------------------------------------
  // 1) Palanca de cambios (requiere embrague pisado; R con bloqueo)
  // ------------------------------------------------------------------
  const req = inp.requestedGear;
  const validManualGear =
    req === 'N' || req === 'R' || (isForwardGear(req) && req <= manualGearCount(spec));
  if (validManualGear && req !== st.gear && req !== st.pendingGear) {
    if (inp.clutch < CLUTCH_FOR_SHIFT) {
      if (st.grindCooldownS === 0) {
        st.grindCooldownS = 0.5;
        events.push({ type: 'gearGrind', requested: req });
        events.push({ type: 'gearDenied', requested: req, reason: 'clutchRequired' });
      }
    } else if (req === 'R' && inp.speedMs > REVERSE_LOCKOUT_MS) {
      if (st.grindCooldownS === 0) {
        st.grindCooldownS = 0.5;
        events.push({ type: 'gearDenied', requested: req, reason: 'moving' });
      }
    } else if (isForwardGear(req) && shaftRpmFor(spec, req, inp.speedMs) > m.redlineRpm * 1.15) {
      // Protección de sincronizado: una marcha imposiblemente corta no entra
      if (st.grindCooldownS === 0) {
        st.grindCooldownS = 0.5;
        events.push({ type: 'gearDenied', requested: req, reason: 'moving' });
      }
    } else {
      st.pendingGear = req;
      st.shiftTimerS = SHIFT_TIME_S;
    }
  }
  if (st.pendingGear !== null) {
    st.shiftTimerS -= dt;
    if (st.shiftTimerS <= 0) {
      st.gear = st.pendingGear;
      st.pendingGear = null;
      events.push({ type: 'gearChanged', gear: st.gear });
    }
  }

  // Mientras la palanca viaja, la caja está en vacío
  const gearNow: Gear = st.pendingGear !== null ? 'N' : st.gear;
  const inGear = gearNow !== 'N';
  const dir = gearNow === 'R' ? -1 : 1;
  const ratio = gearRatioOf(spec, gearNow);

  // ------------------------------------------------------------------
  // 2) Embrague: el acoplamiento real sigue al pedal con retardo corto
  // ------------------------------------------------------------------
  const target = clutchTarget(inp.clutch, m);
  st.engagement += clamp(target - st.engagement, -8 * dt, 8 * dt);
  const engagement = inGear ? st.engagement : 0;
  const clutchCapNm = engagement * m.clutch.maxTorqueNm;

  const shaftRpm = inGear ? shaftRpmFor(spec, gearNow, inp.speedMs) : 0;
  let slip = st.rpm - shaftRpm;

  // ------------------------------------------------------------------
  // 3) Motor: par indicado, ralentí, fricción, starter y calado
  // ------------------------------------------------------------------
  const maxTq = spec.maxTorqueNm;
  let cranking = false;

  let tInd = 0; // par de combustión (N·m)
  let tIdle = 0; // gobernador de ralentí
  let tFric; // fricción interna / bombeo
  if (st.engineOn) {
    const revLimited = st.rpm >= m.redlineRpm;
    tInd = revLimited ? 0 : inp.throttle * maxTq * curveTorqueFrac(m.torqueCurve, st.rpm);
    tFric = maxTq * (0.03 + 0.06 * (st.rpm / m.redlineRpm));
    // El ralentí inyecta par para sostener las rpm mínimas (hasta su límite)
    const idleErr = clamp((m.idleRpm + 40 - st.rpm) / 260, 0, 1);
    tIdle = idleErr * maxTq * 0.34;
  } else {
    // Motor parado: compresión (retiene si algo lo arrastra)
    tFric = maxTq * (0.2 + 0.08 * (st.rpm / m.redlineRpm));
    if (inp.ignition) {
      const interlockOk = inp.clutch >= CLUTCH_FOR_SHIFT || gearNow === 'N';
      if (!interlockOk) {
        if (st.crankBlockCooldownS === 0) {
          st.crankBlockCooldownS = 1.2;
          events.push({ type: 'crankBlocked' });
        }
      } else {
        cranking = true;
        st.crankTimerS += dt;
        // El starter mantiene el motor girando a ~250 rpm con un tembleque
        st.rpm = Math.max(st.rpm, 230 + 50 * Math.sin(st.judderTick * 0.9));
        if (st.crankTimerS >= CRANK_TIME_S) {
          st.engineOn = true;
          st.crankTimerS = 0;
          st.rpm = m.idleRpm + 350;
          events.push({ type: 'engineStart' });
        }
      }
    } else {
      st.crankTimerS = 0;
    }
    // Arranque "del tirón": rodando en marcha con embrague suelto
    if (!st.engineOn && inp.ignition && engagement > 0.7 && Math.abs(shaftRpm) > 550) {
      st.engineOn = true;
      st.crankTimerS = 0;
      events.push({ type: 'engineStart' });
    }
  }

  // ------------------------------------------------------------------
  // 4) Acoplamiento embrague ↔ ruedas
  // ------------------------------------------------------------------
  let driveTqGear = 0; // par entregado al lado caja (N·m, + en sentido de la marcha)
  const netEngineTq = tInd + tIdle - tFric;
  const canLock =
    inGear &&
    Math.abs(slip) < LOCK_SLIP_RPM &&
    Math.abs(netEngineTq) <= clutchCapNm &&
    (shaftRpm >= m.stallRpm || !st.engineOn);

  if (canLock) {
    // Embrague cerrado sin deslizamiento: motor y caja giran solidarios
    st.rpm = Math.max(0, shaftRpm);
    slip = 0;
    if (st.engineOn) {
      driveTqGear = netEngineTq;
    } else {
      // Compresión del motor parado: solo se opone al movimiento
      driveTqGear = Math.abs(shaftRpm) < 30 ? 0 : -Math.sign(shaftRpm) * tFric;
    }
  } else {
    // Deslizando (o en vacío): la fricción se opone al deslizamiento
    const clutchTqOnEngine = inGear ? clamp(slip * 4, -clutchCapNm, clutchCapNm) : 0;
    driveTqGear = clutchTqOnEngine;
    const inertia = m.engineInertia;
    const dOmega = ((netEngineTq - clutchTqOnEngine) / inertia) * dt; // rad/s²·s
    st.rpm += dOmega * (60 / (2 * Math.PI));
    if (!st.engineOn && !cranking) {
      st.rpm = Math.max(0, st.rpm - 2400 * dt); // el motor parado se detiene
    }
    st.rpm = Math.max(0, st.rpm);
  }

  // Sobrerrégimen: corte + aviso (p. ej. reducción a marcha demasiado corta)
  let overRev = false;
  if (st.rpm > m.redlineRpm + 150) {
    overRev = true;
    st.rpm = Math.min(st.rpm, m.redlineRpm + 650);
    if (st.overRevCooldownS === 0 && st.rpm > m.redlineRpm + 350) {
      st.overRevCooldownS = 1.5;
      events.push({ type: 'overRev' });
    }
  }

  // Calado: el motor en marcha cae por debajo del régimen mínimo
  if (st.engineOn && st.rpm < m.stallRpm) {
    st.engineOn = false;
    st.stallCount++;
    st.crankTimerS = 0;
    events.push({ type: 'stall' });
  }

  // ------------------------------------------------------------------
  // 5) Fuerza en rueda, temblores y desgaste del embrague
  // ------------------------------------------------------------------
  let force = (driveTqGear * ratio * DRIVELINE_EFF) / spec.dims.wheelRadiusM;

  // Temblor pre-calado: el coche protesta antes de morir (feedback clave)
  const nearStall = st.engineOn && st.rpm < m.stallRpm + 260 && engagement > 0.25 && inGear;
  if (nearStall) force *= 1 + 0.25 * Math.sin(st.judderTick * 0.85);

  // "Ahogar" el motor: marcha larga, pocas vueltas y pidiendo par
  const lugging =
    st.engineOn &&
    inGear &&
    isForwardGear(gearNow) &&
    gearNow >= 2 &&
    slip === 0 &&
    st.rpm < m.idleRpm + 380 &&
    inp.throttle > 0.3;
  if (lugging) force *= 1 + 0.1 * Math.sin(st.judderTick * 0.95);

  // Calor del embrague al patinar con carga
  if (inGear && Math.abs(slip) > 180 && engagement > 0.05 && st.engineOn) {
    st.clutchHeat = clamp(
      st.clutchHeat + (Math.abs(slip) * engagement) / 1500 / 60,
      0,
      1.25,
    );
  } else {
    st.clutchHeat = Math.max(0, st.clutchHeat - 0.055 * dt);
  }

  // Dirección de la marcha y límite de agarre del eje
  force *= dir;
  let wheelspin = false;
  if (Math.abs(force) > inp.gripCapN) {
    force = Math.sign(force) * inp.gripCapN;
    wheelspin = true;
  }

  // ------------------------------------------------------------------
  // 6) Indicador de cambio (GSI)
  // ------------------------------------------------------------------
  const shiftUpHint =
    st.engineOn &&
    isForwardGear(gearNow) &&
    gearNow < manualGearCount(spec) &&
    st.rpm > shiftUpRpm(m) &&
    inp.throttle > 0.08;
  const shiftDownHint =
    st.engineOn &&
    isForwardGear(gearNow) &&
    gearNow >= 2 &&
    st.rpm < m.idleRpm + 320 &&
    inp.speedMs * dir > 1.5;

  return {
    driveForceN: force,
    rpm: st.rpm,
    events,
    engagement,
    slipRpm: inGear ? slip : 0,
    atBite: inBiteZone(inp.clutch, m),
    cranking,
    lugging,
    overRev,
    shiftUpHint,
    shiftDownHint,
    wheelspin,
  };
}
